import React from "react";
import { Switch, Route } from "react-router-dom";
import { QueryLoader } from "@jsq/ds/core";
import {
  AccountTypeEnum,
  useGetComplianceCasesByObjectIdQuery,
} from "../../graphql/supergraph.index";
import { BeneficialOwnersScreen } from "../BeneficialOwnersScreen";
import { ControllersScreen } from "../ControllersScreen";
import { DocumentCollectorInvestor } from "../documents/DocumentCollectorInvestor";
import { HybridCategorizationPage } from "../HybridCategorizationPage";
import { IndividualLandingScreen } from "../IndividualLandingScreen";
import { IndividualProfileEditScreen } from "../IndividualProfileEditScreen";
import { InstitutionEditScreen } from "../InstitutionEditScreen";
import { JointLandingScreen } from "../JointLandingScreen";
import { OwnerThresholdContainer } from "../OwnerThresholdContainer";
import { ProfileActionOverviewScreen } from "../ProfileActionOverviewScreen";
import { ProfileTree } from "../ProfileTree";
import { SubscriptionComplianceProps, InitialPageSelectorProps } from "./types";

type InitialPage = "individual" | "joint" | "ira_categorization" | "overview" | "tree";

const getInitialPage = (
  accountType: AccountTypeEnum,
  isIraAccount: boolean | null,
  amlEntityTypeId: number | null | undefined,
  ownersCompleted: boolean,
  controllersCompleted: boolean,
): InitialPage => {
  if (
    ["individual", "self_directed_ira"].includes(accountType) ||
    (accountType === "ira" && isIraAccount === true)
  ) {
    return "individual";
  }
  if (["joint", "joint_other", "tic"].includes(accountType)) {
    return "joint";
  }
  if (accountType === "ira" && isIraAccount === null) {
    return "ira_categorization";
  }
  const surveyCompleted = amlEntityTypeId !== null;
  if (surveyCompleted && ownersCompleted && controllersCompleted) {
    return "tree";
  } else {
    return "overview";
  }
};

const InitialPageSelector: React.FC<InitialPageSelectorProps> = ({
  caseResult,
  basePath,
  arenaId,
  onStepCompletion,
}) => {
  const complianceCase = caseResult.data?.complianceCasesByObjectId[0];
  const areOwnersCompleted =
    !!complianceCase?.investingProfile?.caseDataEntryStatus?.ownersCompleted;
  const areControllersCompleted =
    !!complianceCase?.investingProfile?.caseDataEntryStatus?.controllersCompleted;
  const isIra = complianceCase?.investingProfile?.accountType === "ira";
  // Find subscription ID from external objects
  const subscriptionExternalObject = complianceCase?.externalObjects?.find(
    (obj) => obj.objectTypeEnum === "subscription",
  );
  const subscriptionId = subscriptionExternalObject
    ? parseInt(subscriptionExternalObject.objectId)
    : 0;
  switch (
    getInitialPage(
      complianceCase?.investingProfile?.accountType as AccountTypeEnum,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
      complianceCase?.investingProfile?.metaData?.is_ira_account ?? null,
      complianceCase?.investingProfile?.amlEntityTypeId,
      areOwnersCompleted,
      areControllersCompleted,
    )
  ) {
    case "individual":
      return (
        <IndividualLandingScreen
          caseId={complianceCase?.id}
          profileId={complianceCase?.investingProfile?.id}
          showLinkToIraSelector={isIra}
          basePath={basePath}
          arenaId={arenaId}
          subscriptionId={subscriptionId}
          onStepCompletion={onStepCompletion}
        />
      );
    case "joint":
      return (
        <JointLandingScreen
          caseId={complianceCase?.id}
          onStepCompletion={onStepCompletion}
          arenaId={arenaId}
          subscriptionId={subscriptionId}
        />
      );
    case "ira_categorization":
      return (
        <HybridCategorizationPage
          caseId={complianceCase?.id?.toString() || ""}
          profileId={complianceCase?.investingProfile?.id}
          basePath={basePath}
          caseResult={caseResult}
        />
      );
    case "overview":
      return (
        <ProfileActionOverviewScreen
          profileId={complianceCase?.investingProfile?.id}
          basePath={basePath}
          caseResult={caseResult}
          caseId={complianceCase?.id?.toString() || ""}
          showLinkToIraSelector={isIra}
        />
      );
    case "tree":
      return (
        <ProfileTree
          caseId={complianceCase?.id?.toString() || ""}
          basePath={basePath}
          arenaId={arenaId}
          subscriptionId={subscriptionId}
          onStepCompletion={onStepCompletion}
        />
      );
    default:
      throw new Error("Error determining initial page.");
  }
};

/**
 * SubscriptionCompliance root component
 *
 * A router component that provides routes to all compliance components
 */
export const SubscriptionCompliance: React.FC<SubscriptionComplianceProps> = ({
  basePath,
  arenaId,
  prospectId,
  onStepCompletion,
}) => {
  const result = useGetComplianceCasesByObjectIdQuery({
    variables: {
      arenaId,
      externalObject: {
        objectTypeEnum: "prospect",
        objectId: prospectId.toString(),
        service: "main",
      },
    },
    fetchPolicy: "no-cache",
  });

  const caseId = result.data?.complianceCasesByObjectId[0]?.id?.toString() || "";

  return (
    <QueryLoader result={result}>
      <Switch>
        <Route path={`${basePath}/overview/:profileId`} exact>
          <ProfileActionOverviewScreen
            basePath={basePath}
            caseId={caseId}
            caseResult={result}
            showBreadcrumb
          />
        </Route>
        <Route path={basePath} exact>
          <InitialPageSelector
            caseResult={result}
            basePath={basePath}
            arenaId={arenaId}
            onStepCompletion={onStepCompletion}
          />
        </Route>
        <Route path={`${basePath}/edit-institution/:profileId`} exact>
          <InstitutionEditScreen caseId={caseId} caseResult={result.data} basePath={basePath} />
        </Route>
        <Route path={`${basePath}/ira-categorization`} exact>
          <HybridCategorizationPage
            caseId={caseId}
            caseResult={result}
            basePath={basePath}
            profileId={result.data?.complianceCasesByObjectId[0]?.investingProfile?.id}
          />
        </Route>
        <Route path={`${basePath}/edit-individual/:profileId`} exact>
          <IndividualProfileEditScreen caseResult={result.data} basePath={basePath} />
        </Route>
        <Route path={`${basePath}/owners/threshold-check/:profileId`} exact>
          <OwnerThresholdContainer
            caseId={caseId}
            basePath={basePath}
            caseResult={result.data}
          />
        </Route>
        <Route path={`${basePath}/controllers/:profileId`} exact>
          <ControllersScreen caseResult={result.data} basePath={basePath} arenaId={arenaId} />
        </Route>
        <Route path={`${basePath}/owners/:profileId`} exact>
          <BeneficialOwnersScreen
            caseResult={result.data}
            basePath={basePath}
            arenaId={arenaId}
          />
        </Route>
        <Route path={`${basePath}/documents/:profileId`} exact>
          <DocumentCollectorInvestor caseId={parseInt(caseId)} basePath={basePath} />
        </Route>
      </Switch>
    </QueryLoader>
  );
};
